import React, { useState } from 'react';
import { Box, Chip, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { stopPropagation } from '../NodeUI/nodeUiStyles';
import { stringAvatar } from '../../utils/AvatarUtils';
import AgentAvatar from './AgentAvatar';
import { getToolLabel } from './agentToolLabels';

export const AGENT_MODEL_LABELS = {
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
  'gpt-4.1': 'GPT-4.1',
  'gpt-4.1-mini': 'GPT-4.1 Mini',
  'o3-mini': 'o3-mini',
  'claude-sonnet-4-20250514': 'Claude Sonnet 4',
  'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
  'claude-3-7-sonnet-20250219': 'Claude 3.7 Sonnet',
  'gemini-2.0-flash': 'Gemini 2.0 Flash',
};

const MODEL_LABELS = AGENT_MODEL_LABELS;

/**
 * Rich read-only summary card for a selected agent (avatar, description, inbox,
 * model, tools, id). Shared by the AI Agent node config and the board stage
 * editor. Pass `onConfigure` to show the gear button that opens a config drawer;
 * omit it to render a display-only card.
 */
const MAX_TOOL_CHIPS = 4;

const InlineAgentCard = React.memo(function InlineAgentCard({ agent, onConfigure, compact = false }) {
  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleCopyEmail = (e) => {
    e.stopPropagation();
    if (!agent.inboxAddress) return;
    navigator.clipboard.writeText(agent.inboxAddress);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const modelLabel = MODEL_LABELS[agent.model] || agent.model || 'No model';
  const toolCount = agent.enabledTools ? agent.enabledTools.length : 0;
  const accentColor = stringAvatar(agent.name || 'Agent').sx?.bgcolor || '#71717a';
  const shortId = agent.agentId ? agent.agentId.slice(0, 8).toUpperCase() : '';

  if (compact) {
    return (
      <Box
        onMouseDown={stopPropagation}
        onClick={stopPropagation}
        onPointerDown={stopPropagation}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ height: '2px', width: '100%', backgroundColor: accentColor }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1 }}>
          <AgentAvatar
            agent={agent}
            name={agent.name}
            size={32}
            borderRadius="8px"
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.3 }} noWrap>
              {agent.name || 'Untitled Agent'}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.3 }} noWrap>
              {modelLabel}
              {toolCount > 0
                ? ` · ${toolCount} tool${toolCount !== 1 ? 's' : ''}`
                : ' · No tools'}
            </Typography>
          </Box>
          {onConfigure && (
            <Tooltip title="Configure agent">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onConfigure();
                }}
                sx={{ flexShrink: 0, width: 26, height: 26, color: 'text.secondary' }}
              >
                <SettingsOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ height: '3px', width: '100%', backgroundColor: accentColor }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, pt: 1.5, pb: 1 }}>
        <AgentAvatar agent={agent} name={agent.name} size={36} borderRadius="8px" sx={{ boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }} noWrap>
            {agent.name || 'Untitled Agent'}
          </Typography>
          {agent.description ? (
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }} noWrap>
              {agent.description}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '11px', color: 'text.disabled', fontStyle: 'italic', mt: 0.25 }}>
              No description
            </Typography>
          )}
        </Box>
        {onConfigure && (
          <Tooltip title="Configure agent">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onConfigure(); }}
              sx={{
                flexShrink: 0,
                width: 28,
                height: 28,
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
              }}
            >
              <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ mx: 1.5 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 1.5, py: 1 }}>
        {agent.inboxAddress ? (
          <Tooltip title={copiedEmail ? 'Copied!' : 'Click to copy'} placement="bottom-start">
            <Box
              onClick={handleCopyEmail}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: 'pointer',
                px: 0.75,
                py: 0.25,
                mx: -0.75,
                borderRadius: '4px',
                border: '1px solid transparent',
                transition: 'all 150ms ease',
                ...(copiedEmail
                  ? { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }
                  : { '&:hover': { backgroundColor: 'action.hover', borderColor: 'divider' } }),
              }}
            >
              <EmailOutlinedIcon sx={{ fontSize: 13, color: copiedEmail ? '#15803d' : 'text.disabled', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '11px', color: copiedEmail ? '#15803d' : 'text.secondary', flex: 1 }} noWrap>
                {agent.inboxAddress}
              </Typography>
              {copiedEmail ? (
                <CheckIcon sx={{ fontSize: 11, color: '#15803d', flexShrink: 0 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0, opacity: 0, '.MuiBox-root:hover &': { opacity: 1 } }} />
              )}
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <EmailOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '11px', color: 'text.disabled', fontStyle: 'italic' }}>
              No inbox assigned
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <SmartToyOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{modelLabel}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <BuildOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Tools</Typography>
        </Box>
        {toolCount > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {agent.enabledTools.slice(0, MAX_TOOL_CHIPS).map((toolName) => (
              <Chip
                key={toolName}
                label={getToolLabel(toolName)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '10px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  backgroundColor: 'action.hover',
                  color: 'text.secondary',
                }}
              />
            ))}
            {toolCount > MAX_TOOL_CHIPS && (
              <Chip
                label={`+${toolCount - MAX_TOOL_CHIPS} more`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '10px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  color: 'text.disabled',
                  borderColor: 'divider',
                }}
              />
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '10px', color: 'text.disabled', fontStyle: 'italic', pl: 2.5 }}>
            No tools enabled
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          backgroundColor: 'action.hover',
          borderTop: '1px solid',
          borderTopColor: 'divider',
        }}
      >
        <Typography
          sx={{
            fontSize: '9px',
            fontFamily: "'SF Mono', 'Menlo', 'Monaco', monospace",
            color: 'text.disabled',
            letterSpacing: '0.5px',
          }}
        >
          ID: {shortId}
        </Typography>
      </Box>
    </Box>
  );
});

export default InlineAgentCard;
